import { useCallback, useEffect, useRef, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { ConfigService } from "@/shared/services/config.service";
import { useTheme } from "@/shared/theme/useTheme";

import { loadTurnstile, type TurnstileAction, turnstileLanguage } from "../utils/turnstile";

export interface TurnstileController {
  /** False when the environment has no site key: no widget, no token, nothing blocks the form. */
  enabled: boolean;
  token: string | null;
  /** The widget could not load or the check failed for good; the form cannot be sent until a reload. */
  failed: boolean;
  /** Whether the form may be sent: always when disabled, otherwise only with a token in hand. */
  ready: boolean;
  attachContainer: (element: HTMLDivElement | null) => void;
  /** Tokens are single-use: call after every request that carried one. */
  reset: () => void;
}

export function useTurnstile(action: TurnstileAction): TurnstileController {
  const siteKey = ConfigService.getInstance().get("turnstileSiteKey") as string | undefined;
  const enabled = !!siteKey;
  const { activeLanguage } = useTranslations("login");
  const { theme } = useTheme();

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !container) return;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled) return;
        widgetIdRef.current =
          turnstile.render(container, {
            sitekey: siteKey,
            action,
            appearance: "interaction-only",
            theme: theme === "dark" ? "dark" : "light",
            size: "flexible",
            language: turnstileLanguage(activeLanguage),
            callback: (value) => {
              setFailed(false);
              setToken(value);
            },
            "expired-callback": () => setToken(null),
            "timeout-callback": () => setToken(null),
            "error-callback": () => {
              setToken(null);
              setFailed(true);
            },
          }) ?? null;
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      setToken(null);
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [siteKey, container, action, theme, activeLanguage]);

  const reset = useCallback(() => {
    setToken(null);
    const widgetId = widgetIdRef.current;
    if (widgetId) window.turnstile?.reset(widgetId);
  }, []);

  return {
    enabled,
    token,
    failed,
    ready: !enabled || !!token,
    attachContainer: setContainer,
    reset,
  };
}
